import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../css/Ui.css';
import axios from 'axios';
import { getCurrentComCode, getCurrentUser } from '../api/common';
import { getDept } from '../api/deptApi';
import { getLeftMenuItems, getLeftMenus, getManager } from '../api/managerApi';
import { parseManagerSelectedMenus, setManagerMenuItemsFromDb } from '../utils/managerUtils';
import {
  buildMenuConfigFromDb,
  DEFAULT_MENU_MAP,
  getCategoryMenuKeys,
  renderMenuById,
} from '../pages/manager/items/SideMenuList';

const DEFAULT_REDIRECT_ORDER = ['myDesk', 'mail', 'approval', 'board', 'reservation', 'schedule', 'todolist'];

const withCacheBuster = (src, version) => {
  if (!src) return src;
  return `${src}${src.includes('?') ? '&' : '?'}v=${version}`;
};


function Sidebar({ collapsed, onOpenFormModal }) {
  const navigate = useNavigate();
  const token = sessionStorage.getItem('token');
  const decoded = token ? JSON.parse(atob(token.split('.')[1])) : null;
  const isAdmin = decoded?.role === 'admin';

  const [openMenu, setOpenMenu] = useState(null);
  const [openAdminSubMenu, setOpenAdminSubMenu] = useState(null);
  const [sidebarInfo, setSidebarInfo] = useState(null);
  const [logoImageVersion, setLogoImageVersion] = useState(Date.now());
  const [menuConfigMap, setMenuConfigMap] = useState(DEFAULT_MENU_MAP);
  const [menuReady, setMenuReady] = useState(false);
  const redirectedRef = useRef(false);
  const location = useLocation();

  const categoryMenuKeys = useMemo(
    () => getCategoryMenuKeys(menuConfigMap),
    [menuConfigMap]
  );

  // 동적으로 카테고리 메뉴별 ref 생성
  const categoryMenuRefs = useRef({});
  const ensureRef = (menuKey) => {
    if (!categoryMenuRefs.current[menuKey]) {
      categoryMenuRefs.current[menuKey] = React.createRef();
    }
    return categoryMenuRefs.current[menuKey];
  };

  // 동적으로 카테고리 메뉴별 position state 생성 및 관리
  const [categoryMenuPositions, setCategoryMenuPositions] = useState({});

  useEffect(() => {
    setCategoryMenuPositions((prev) => {
      const next = {};
      categoryMenuKeys.forEach((key) => {
        next[key] = prev[key] || { top: 0 };
      });
      return next;
    });
  }, [categoryMenuKeys]);

  // admin 메뉴 (하드코딩)
  const adminBtnRef = useRef(null);
  const [adminPos, setAdminPos] = useState({ top: 0 });
  const [sessionValue, setSessionValue] = useState(null);
  const sidebarRef = useRef(null);

  useEffect(() => {
    setOpenMenu(null);
    const session = sessionStorage.getItem("session");
    setSessionValue(session);
  }, [collapsed]);

  useEffect(() => {
    const params = new URLSearchParams({
      iam_sso: '1',
      client_id: 'yF7diW8SjD7m0dp2Lq8qraBHVUQmrVTCzZAgHwLO',
      redirect_uri: 'https://mail.simgl.co.kr',
      response_type: 'code',
      scope: 'openid profile email',
      prompt: 'none',
    });

    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'display:none;width:0;height:0;';
    iframe.title = 'mail-sso';
    iframe.onload = () => {
      window.mailSsoReady = true;
      window.dispatchEvent(new CustomEvent('mail-sso-ready'));
    };
    iframe.src = `https://mail.simgl.co.kr/?${params}`;

    const container = sidebarRef.current;
    if (container) {
      container.appendChild(iframe);
    }

    return () => {
      if (container && container.contains(iframe)) {
        container.removeChild(iframe);
      }
    };
  }, []);

  const fetchSidebarInfo = useCallback(async () => {
    try {
      const M_ComCode = getCurrentComCode();
      if (!M_ComCode) {
        console.error('사이드바 매니저 정보 조회 실패: M_ComCode가 없습니다.');
        return;
      }
      console.log('Fetching sidebar info with M_ComCode:', M_ComCode);
      const data = await getManager(M_ComCode);
      setSidebarInfo(data);
      setLogoImageVersion(Date.now());
    } catch (err) {
      console.error('사이드바 매니저 정보 조회 실패:', err);
    }
  }, []);

  useEffect(() => {
    fetchSidebarInfo();
    window.addEventListener('managerDataChanged', fetchSidebarInfo);
    return () => window.removeEventListener('managerDataChanged', fetchSidebarInfo);
  }, [fetchSidebarInfo]);

  const fetchMenuConfig = useCallback(async () => {
    try {
      const [menuRows, itemRows] = await Promise.all([getLeftMenus(token), getLeftMenuItems(token)]);
      setManagerMenuItemsFromDb(menuRows);
      const dbConfig = buildMenuConfigFromDb(menuRows, itemRows, DEFAULT_MENU_MAP);
      setMenuConfigMap(dbConfig);
    } catch (err) {
      console.error('사이드바 메뉴 설정 조회 실패:', err);
      setMenuConfigMap(DEFAULT_MENU_MAP);
    } finally {
      setMenuReady(true);
    }
  }, [token]);

  useEffect(() => {
    fetchMenuConfig();
    window.addEventListener('leftMenuChanged', fetchMenuConfig);
    return () => window.removeEventListener('leftMenuChanged', fetchMenuConfig);
  }, [fetchMenuConfig]);

  const [boardCodes, setBoardCodes] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [filteredBoardCodes, setFilteredBoardCodes] = useState([]);
  useEffect(() => {
    const fetchBoardCodes = async () => {
      try {
        const res = await axios.get('/api/board-codes');
        //const data = await res.json();
        setBoardCodes(res.data);
      } catch (error) {
        console.error("게시판 코드 불러오기 실패:", error);
      }
    };
    fetchBoardCodes();
  }, []);

  const fetchCurrentUser = useCallback(async () => {
    if (!token) return;

    try {
      const userData = await getCurrentUser(token);
      setCurrentUser(userData);
    } catch (err) {
      console.error('현재 사용자 정보 조회 실패:', err);
      setCurrentUser(null);
    }
  }, [token]);

  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);


  const checkBoardAccess = useCallback(async (codeId) => {
    // Admin 사용자는 항상 가능
    if (currentUser?.role === 'admin') return true;

    try {
      const res = await axios.get(`/api/board-codes/auths/board-code-id/${codeId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const permissions = Array.isArray(res.data) ? res.data : [];

      if (!permissions || permissions.length === 0) return true;

      const hasReadPermissions = permissions.some(p => p.auth_type === 'read');

      if (!hasReadPermissions) return true;

      let hasAccess = false;
      for (const permission of permissions) {
        if (permission.target_type === 'user' && String(permission.target_id) === String(currentUser?.userid)) {
          hasAccess = true;
          break;
        }

        if (permission.target_type === 'dept') {
          if (String(permission.target_id) === String(currentUser?.dept_code)) {
            hasAccess = true;
            break;
          }

          try {
            const deptData = await getDept(permission.target_id);
            if (deptData?.data?.full_child_dept_code) {
              const childDeptCodes = deptData.data.full_child_dept_code.split(',');
              if (childDeptCodes.includes(String(currentUser?.dept_code))) {
                hasAccess = true;
                break;
              }
            }
          } catch (err) {
            console.error('부서 정보 조회 실패:', err);
          }
        }
      }

      return hasAccess;
    } catch (err) {
      console.error('권한 조회 실패:', err);
      return false;
    }
  }, [currentUser, token]);

  const updateButtonTop = useCallback(() => {
    if (collapsed) {
      // 동적 카테고리 메뉴 처리
      categoryMenuKeys.forEach((menuKey) => {
        if (openMenu === menuKey) {
          const btnRef = categoryMenuRefs.current[menuKey];
          if (btnRef?.current) {
            const rect = btnRef.current.getBoundingClientRect();
            setCategoryMenuPositions(prev =>
              prev[menuKey].top !== rect.top
                ? { ...prev, [menuKey]: { top: rect.top } }
                : prev
            );
          }
        }
      });

      // admin 메뉴 처리 (하드코딩)
      if (openMenu === 'admin' && adminBtnRef.current) {
        const rect = adminBtnRef.current.getBoundingClientRect();
        setAdminPos(prev => prev.top !== rect.top ? { top: rect.top } : prev);
      }
    }
  }, [collapsed, openMenu, categoryMenuKeys]);

  useEffect(() => {
    updateButtonTop();
    const onScrollResize = () => updateButtonTop();
    window.addEventListener('scroll', onScrollResize, { passive: true });
    window.addEventListener('resize', onScrollResize);
    return () => {
      window.removeEventListener('scroll', onScrollResize);
      window.removeEventListener('resize', onScrollResize);
    };
  }, [updateButtonTop]);

  useEffect(() => {
    if (currentUser && boardCodes.length > 0) {
      const doFilter = async () => {
        const filtered = [];
        for (const board of boardCodes) {
          const hasAccess = await checkBoardAccess(board.id);
          if (hasAccess) filtered.push(board);
        }
        setFilteredBoardCodes(filtered);
      };
      doFilter();
    }
  }, [currentUser, boardCodes, checkBoardAccess]);

  useEffect(() => {
    if (location.pathname !== '/') {
      redirectedRef.current = false;
      return;
    }
    if (!menuReady) return;
    if (redirectedRef.current) return;

    const availableIds = Object.keys(menuConfigMap);
    const preferred = sidebarInfo?.P_SelectMenus
      ? parseManagerSelectedMenus(sidebarInfo.P_SelectMenus).map(m => m.id)
      : DEFAULT_REDIRECT_ORDER;
    const order = preferred.filter(id => availableIds.includes(id));
    const finalOrder = order.length > 0 ? order : availableIds;

    // finalOrder 순서대로 menuConfigMap 항목 정렬
    const sortedEntries = finalOrder.map(id => ({ id, ...(menuConfigMap[id] || {}) }));

    console.log('[Sidebar redirect] finalOrder:', finalOrder);
    console.log('[Sidebar redirect] sortedEntries:', sortedEntries.map(e => ({ id: e.id, type: e.type, path: e.path })));

    for (const entry of sortedEntries) {
      const { id } = entry;
      const type = String(entry.type || '').trim().toLowerCase() || 'link';
      const path = entry.path || '';
      const items = entry.items;

      console.log(`[Sidebar redirect] checking id="${id}" type="${type}" path="${path}"`);

      // 1. link 타입: path로 이동
      if (type === 'link') {
        console.log(`[Sidebar redirect] → link → navigating to "${path}"`);
        redirectedRef.current = true;
        navigate(path, { replace: true });
        return;
      }

      // 2. url 타입: 스킵
      if (type === 'url') {
        console.log(`[Sidebar redirect] → url → skip`);
        continue;
      }

      // 3/4. category 타입
      if (type === 'category') {
        if (!Array.isArray(items) || items.length === 0) {
          console.log(`[Sidebar redirect] → category, no items → skip`);
          continue;
        }
        const firstPath = items.find(item => item?.path)?.path;
        if (!firstPath) {
          console.log(`[Sidebar redirect] → category, items have no path → skip`);
          continue;
        }
        console.log(`[Sidebar redirect] → category → navigating to first item path "${firstPath}"`);
        redirectedRef.current = true;
        navigate(firstPath, { replace: true });
        return;
      }
    }

    // 5. 이동 가능한 메뉴 없음 → /portal로 이동
    console.log('[Sidebar redirect] no navigable menu found → navigating to "/portal"');
    redirectedRef.current = true;
    navigate('/portal', { replace: true });
  }, [location.pathname, menuReady, menuConfigMap, sidebarInfo, navigate]);

  const handleLogout = async () => {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('session');
    sessionStorage.removeItem('M_ComCode');
    setOpenMenu(null);
    try {
      const res = await fetch('/api/auth/authentik/logout');
      const { logoutUrl } = await res.json();
      if (logoutUrl && new URL(logoutUrl).searchParams.has('id_token_hint')) {
        window.location.href = logoutUrl;
        return;
      }
    } catch (_) { }
    navigate('/login');
  };

  const toggleMenu = (menuName) => {
    setOpenMenu(openMenu === menuName ? null : menuName);
  };

  // 아직 개발되지 않은 메뉴 클릭 시
  const handlePendingNavigate = () => {
    alert('준비중입니다');
    setOpenMenu(null);
  };

  const handleSimpleNavigate = (path, menu) => {
    const target = String(path || '').trim();
    if (/^https?:\/\//i.test(target)) {
      window.open(target, '_blank', 'noopener,noreferrer');
      setOpenMenu(collapsed ? null : menu || null);
      return;
    }

    setOpenMenu(collapsed ? null : menu || null);
    navigate(target);
  };

  const DEFAULT_MENU_ORDER = ['myDesk', 'mail', 'approval', 'board', 'reservation', 'schedule', 'todolist'];
  const availableMenuIds = Object.keys(menuConfigMap);
  const preferredOrder = sidebarInfo?.P_SelectMenus
    ? parseManagerSelectedMenus(sidebarInfo.P_SelectMenus).map(m => m.id)
    : DEFAULT_MENU_ORDER;
  const menuOrder = preferredOrder.filter((id) => availableMenuIds.includes(id));
  const finalMenuOrder = menuOrder.length > 0 ? menuOrder : availableMenuIds;

  // 동적 menuItemCtx 생성: 각 카테고리 메뉴에 ref와 position 추가
  const menuItemCtx = {
    collapsed, sessionValue, openMenu,
    adminPos,
    adminBtnRef,
    handleSimpleNavigate, toggleMenu, setOpenMenu,
    handlePendingNavigate, onOpenFormModal,
    dataSources: {
      filteredBoardCodes,
    },
  };

  // 카테고리 메뉴별 ref와 position 동적 추가
  categoryMenuKeys.forEach((menuKey) => {
    const ref = ensureRef(menuKey);
    menuItemCtx[`${menuKey}BtnRef`] = ref;
    menuItemCtx[`${menuKey}Pos`] = categoryMenuPositions[menuKey] || { top: 0 };
  });

  const renderDynamicMenuById = (menuId) => renderMenuById(menuId, menuItemCtx, menuConfigMap);

  return (
    <aside ref={sidebarRef} className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
      <div className="logo">
        {collapsed ? (
          <img
            src={sidebarInfo?.P_SLogoPath
              ? withCacheBuster(sidebarInfo.P_SLogoPath, logoImageVersion)
              : '/images/tni_logo_s.png'}
            alt="축소 로고"
            className="logo-img"
          />
        ) : (
          <img
            src={sidebarInfo?.P_LogoPath
              ? withCacheBuster(sidebarInfo.P_LogoPath, logoImageVersion)
              : '/images/tni_logo.png'}
            alt="기본 로고"
            className="logo-img"
          />
        )}
      </div>

      <div className="nav flex-column">
        {menuReady && finalMenuOrder.map(id => renderDynamicMenuById(id))}

        {/* 관리자 */}
        {isAdmin && (
          <div className="nav-item">
            <button
              ref={adminBtnRef}
              className="btn btn-link nav-link text-info w-100 text-start d-flex justify-content-between align-items-center"
              onClick={() => toggleMenu('admin')}
            >
              <span>
                <i className="fas fa-cogs me-2"></i>
                {!collapsed && '관리자'}
              </span>
              {!collapsed && (
                <i className={`fas fa-chevron-${openMenu === 'admin' ? 'down' : 'right'}`}></i>
              )}
            </button>
            {openMenu === 'admin' && (
              <ul
                className={`sidebar-submenu list-unstyled ${!collapsed ? 'ps-4' : ''} ${collapsed ? 'floating' : 'open'}`}
                style={
                  collapsed
                    ? { position: 'fixed', top: adminPos.top, left: 80 }
                    : undefined
                }
              >
                <li>
                  <button className="dropdown-item" onClick={() => handleSimpleNavigate('/users', 'admin')}>
                    사용자 관리
                  </button>
                </li>
                <li>
                  <button className="dropdown-item" onClick={() => handleSimpleNavigate('/dept', 'admin')}>
                    부서관리
                  </button>
                </li>
                <li>
                  <button className="dropdown-item" onClick={() => handleSimpleNavigate('/board-codes', 'admin')}>
                    게시판관리
                  </button>
                </li>
                <li>
                  <button className="dropdown-item" onClick={() => handleSimpleNavigate('/resources', 'admin')}>
                    자원관리
                  </button>
                </li>
                <li>
                  <button className="dropdown-item" onClick={() => handleSimpleNavigate('/aprvdoc/admin', 'admin')}>
                    결재문서 보기
                  </button>
                </li>
                <li>
                  <button className="dropdown-item" onClick={() => handleSimpleNavigate('/aprv/forms', 'admin')}>
                    전자결재양식관리
                  </button>
                </li>
                <li>
                  <button className="dropdown-item" onClick={() => handleSimpleNavigate('/risk-cate', 'admin')}>
                    위험성 분류관리-삭제할것
                  </button>
                </li>
                <li>
                  <button className="dropdown-item" onClick={() => handleSimpleNavigate('/safety/master', 'admin')}>
                    위험성평가 설정관리
                  </button>
                </li>
                <li>
                  <button
                    className="dropdown-item d-flex justify-content-between align-items-center"
                    onClick={() => setOpenAdminSubMenu(openAdminSubMenu === 'manager' ? null : 'manager')}
                  >
                    <span>통합관리</span>
                    <i className={`fas fa-chevron-${openAdminSubMenu === 'manager' ? 'down' : 'right'} ms-2`}></i>
                  </button>
                  {openAdminSubMenu === 'manager' && (
                    <ul className="list-unstyled ps-3 mb-0">
                      <li>
                        <button className="dropdown-item" onClick={() => handleSimpleNavigate(`/manager/${getCurrentComCode()}`, 'admin')}>
                          회사관리
                        </button>
                      </li>
                      <li>
                        <button className="dropdown-item" onClick={() => handleSimpleNavigate('/manager/left-menu', 'admin')}>
                          메뉴관리
                        </button>
                      </li>
                      <li>
                        <button className="dropdown-item" onClick={() => handleSimpleNavigate('/manager/portlet', 'admin')}>
                          포틀릿관리
                        </button>
                      </li>
                    </ul>
                  )}
                </li>
              </ul>
            )}
          </div>
        )}
      </div>

      <div className="logout">
        <button
          className="btn btn-outline-warning w-100 text-start"
          onClick={handleLogout}
        >
          <i className="fas fa-sign-out-alt me-2"></i>
          {!collapsed && '로그아웃'}
        </button>
      </div>
    </aside>
  );
}

export default Sidebar;